import moment from 'moment';
import { LaunchDarklyFlags } from '../types/launchDarklyFlags';
import { getLaunchDarklyContext } from '../utils/flags';
import { RateType, NoticeType, Day, LineItemType, Product } from '../enums';
import { InvoiceItemTypeData } from '../enums/InvoiceItemType';
import {
  ESnapshotExists,
  ENotice,
  ERef,
  ERate,
  EOrganization,
  ESnapshot,
  EInvoice,
  MailDelivery,
  FirebaseTimestamp,
  EFirebaseContext,
  exists,
  EDisplayParams,
  DiscountConfig
} from '../types';
import {
  AffidavitReconciliationSettings,
  DistributeSettings
} from '../types/organization';
import { LineItem, OrderInvoice, PublicNoticeInvoice } from '../types/invoices';
import {
  firestoreTimestampOrDateToDate,
  removeUndefinedFields
} from '../helpers';
import { COLUMN_REP_FEE } from '../constants';
import {
  getAffidavitFeeInCentsAndFeeSplitFromSettingsAndMail,
  getAffidavitFeeInCentsFromSettingsAndMail,
  getAffidavitPricingData,
  getShouldApplyColumnManagedAffidavitFees,
  noticeAddOnCoversAffidavit
} from './affidavits';
import { getErrorReporter } from '../utils/errors';
import {
  getAffidavitAdditionalFeeLineItemsFromRate,
  getNonAffidavitAdditionalFeeLineItemsFromAdditionalFees,
  getLastNonFeeAndNonDiscountLineItemIndex,
  getAdditionalFeesFromRate,
  getColorAdditionalFeeLineItems,
  getJobcaseAdditionalFeeLineItem
} from './additionalFees';
import { getOrThrow } from '../utils/refs';
import { DisplayParams } from '../types/notice';
import {
  isPercentAdditionalFee,
  AdditionalFee,
  AdRate,
  isNoticeRate,
  isOrderRate
} from '../types/rates';
import {
  getActiveDiscountConfigForNotice,
  getUpdatedDiscountLineItemsForNotice
} from '../notice/discounts';
import { dbToUICurrencyString } from './ui';
import {
  calculateApplicationFeeInCents,
  calculatePublisherAmountInCents,
  getColumnCentimeters,
  getColumnInches,
  getFolios,
  roundUp
} from './math';
import { oklahoma } from './rateTypes/oklahoma';
import { getInheritedProperty } from '../utils/inheritance';
import {
  NOTICE_ASYNC_DESIGN_FINALIZED,
  NoticeAsyncDesignFinalizedEvent
} from '../types/events';
import {
  FeeSplit,
  NewspaperOrderFeeSplitResults,
  TrackedFeeSplitResult
} from '../types/feeSplit';
import { computePlacementFeeForInvoicePricing } from './expressPlacementFee';
import { ExpressPlacementBilling } from '../types/expressPlacementBilling';
import { fuzzyStringContains } from '../utils/strings';
import findDayRate from './findDayRate';
import calculateSingleRunPrice from './calculateSingleRunPrice';
import getApplicableRate from './getApplicableRate';
import {
  getDistributedLineItems,
  distributeFeeProportionally
} from './distributeFee';
import { NewspaperOrder } from '../types/newspaperOrder';
import { Ad } from '../types/ad';
import { isOrderInvoice } from '../utils/invoices';
import { getPublicationLineItemsForPeriodBasedRate } from './periodBasedPricing';
import { getConvenienceFeeSplitFromRate } from './feeSplitHelpers';
import { PRODUCT_TO_NAME } from '../enums/Product';
import {
  calculateConvenienceFee,
  calculateConvenienceFeeAndFeeSplit,
  getConvenienceFeeSubtotalInCents,
  getInvoiceConvenienceFee,
  maybeGetColumnRepFeePctFromNewspaper,
  rateToConvenienceFee
} from './convenienceFee';
import type { ConvenienceFee } from './convenienceFee';
import { ColumnService } from '../services/directory';
import {
  NoticeAddOnPricing,
  NoticeAddOnPricingContext
} from './noticeAddOns/types';
import {
  NoticeAddOnQuote,
  getContextFromNoticeAddOnQuote
} from './noticeAddOns/quote';
import {
  computeNoticeAddOnPricing,
  expandNoticeAddOnsToLineItems,
  deriveNoticeAddOnPricingContext,
  hasBillableAddOnMail,
  sumNoticeAddOnPricing
} from './noticeAddOns/shared';

// A lot of other files import 'roundUp' and other helpers from here
export * from './math';

export type AffidavitPricingData = {
  mail: MailDelivery[] | undefined;
  settings: AffidavitReconciliationSettings | undefined;
  /**
   * What the filer was quoted for add-ons, frozen at placement. Preferred over
   * `paperLevelSettings` for add-on pricing, which must bill what the filer
   * agreed to rather than what the publisher is configured as today.
   */
  noticeAddOnQuote?: NoticeAddOnQuote | null;
  /**
   * The same settings resolved *without* the notice-level override, i.e. what
   * the paper requires rather than what this notice ended up configured as.
   *
   * Electing a wet-signature affidavit stamps `notarizationVendor: 'manual'`
   * onto the notice, which makes a customer opt-in indistinguishable from a
   * paper that mandates wet sig. Add-on pricing must key its "publisher covers
   * the first copy" waiver off the paper, so it reads this instead of
   * `settings`. Everything else keeps using the full cascade.
   */
  paperLevelSettings?: AffidavitReconciliationSettings | undefined;
  requiresAffidavit: boolean;
};

export const COLUMN_NAME = 'Column, PBC';

export {
  DEFAULT_COLUMN_FEE_PCT,
  calculateConvenienceFee,
  calculateConvenienceFeeAndFeeSplit,
  getConvenienceFeeWithRepFeeFromRateOrNewspaper,
  getInvoiceConvenienceFee
} from './convenienceFee';
export type { ConvenienceFee } from './convenienceFee';

/**
 * Determines how much of the affidavit fee to charge to a particular notice based
 * on the subtotal. Right now we only charge the affidavit fee for notices that cost >$0
 * and have a convenience fee > 0%.
 *
 * TODO: this function should be deprecated once we have a better way of determining the
 * cost of additional fees for notices that cost $0.
 */
const getFinalAffidavitFeeInCents = (options: {
  subtotalInCents: number;
  defaultAffidavitFeeInCents: number;
  convenienceFee: ConvenienceFee;
  affidavitSettings: AffidavitReconciliationSettings | undefined;
  requiresAffidavit: boolean;
  publicationDates: ENotice['publicationDates'] | undefined;
  rate: AdRate | undefined;
}): number => {
  const shouldApplyColumnManagedAffidavitFees =
    getShouldApplyColumnManagedAffidavitFees(
      options.affidavitSettings,
      options.publicationDates,
      options.requiresAffidavit
    );
  if (!shouldApplyColumnManagedAffidavitFees) {
    return 0;
  }

  if (options.subtotalInCents === 0) {
    return 0;
  }

  if (
    options.rate?.noAutoAffidavitFee &&
    options.convenienceFee.convenienceFeePct <= 0
  ) {
    return 0;
  }

  return options.defaultAffidavitFeeInCents;
};

const relevantDisplayParameterFromRate = (
  rateRecord: ERate,
  displayParameters: EDisplayParams,
  columns: number
) => {
  if (rateRecord.rateType === RateType.flat.value) return null;
  if (rateRecord.rateType === RateType.per_run.value) return null;
  if (rateRecord.rateType === RateType.word_count.value)
    return displayParameters.words;
  if (rateRecord.rateType === RateType.inch.value)
    return roundUp(
      displayParameters.height * displayParameters.width,
      rateRecord.roundOff
    ).toFixed(2);
  if (rateRecord.rateType === RateType.column_inch.value)
    return getColumnInches(
      displayParameters.height,
      columns,
      rateRecord.roundOff
    ).toFixed(2);
  if (rateRecord.rateType === RateType.line.value)
    return displayParameters.lines;

  if (rateRecord.rateType === RateType.folio.value)
    return getFolios({
      words: displayParameters.words || 0,
      folioSize: rateRecord.folioSize
    });

  if (rateRecord.rateType === RateType.nebraska.value)
    return displayParameters.lines;
  if (
    [RateType.oklahoma.value, RateType.new_oklahoma.value].includes(
      rateRecord.rateType
    )
  )
    return displayParameters.lines;
  if (rateRecord.rateType === RateType.iowa_form.value)
    return displayParameters.lines;
  if (rateRecord.rateType === RateType.battle_born.value)
    return displayParameters.lines;
  if (rateRecord.rateType === RateType.berthoud_government.value)
    return `${displayParameters.lines} lines, ${getColumnInches(
      displayParameters.height,
      columns,
      rateRecord.roundOff
    ).toFixed(2)} total column inches`;
  if (rateRecord.rateType === RateType.enterprise.value)
    // This is the only rate type where we round after calculating column inches
    return `${roundUp(
      getColumnInches(displayParameters.height, columns, null),
      rateRecord.roundOff
    ).toFixed(2)} total column inches`;
  if (rateRecord.rateType === RateType.single_column_centimetre.value) {
    return `${getColumnCentimeters(
      displayParameters.height,
      columns,
      rateRecord.roundOff
    ).toFixed(2)} total scm`;
  }
  if (rateRecord.rateType === RateType.nypa_statutory_rate.value) {
    return `${displayParameters.lines} lines`;
  }
  throw new Error(`Unknown rate type ${rateRecord.rateType}`);
};

export const floatToDBPercent = (pct: number | string): number => {
  if (pct === null || pct === undefined) {
    return 0;
  }

  const parsedPct = typeof pct === 'string' ? parseFloat(pct) : pct;
  const floatStr = parsedPct.toFixed(2);
  return parseFloat(floatStr);
};

export const calculateSubtotalFromLineItems = (lineItems: LineItem[]) => {
  const subtotal = lineItems.reduce(
    (acc, lineItem) => acc + lineItem.amount,
    0
  );
  // Rounding for safety in case we get some decimal values in the line items
  return Math.round(subtotal);
};

const calculateTaxFromLineItems = (lineItems: LineItem[], taxPct: number) => {
  const taxAmt = lineItems.reduce(
    (acc, lineItem) => acc + Math.round((lineItem.amount * taxPct) / 100),
    0
  );
  return taxAmt;
};

/**
 * Check if two prices are functionally the same (not off by more than 0.5 cents)
 */
export const pricesAreTheSame = (
  amountAInCents: number,
  amountBInCents: number
) => {
  return Math.abs(amountAInCents - amountBInCents) < 0.5;
};

export const getInvoiceAmountsBreakdown = (
  invoiceSnap:
    | ESnapshotExists<PublicNoticeInvoice>
    | ESnapshotExists<OrderInvoice>
): {
  /**
   * The sum of all invoice line-item amounts, including any additional fees collected by the publisher (excluding sales tax!)
   */
  subtotalInCents: number;

  /**
   * The monetary amount calculated by (1) multiplying each taxable line item by the tax percentage expressed as a decimal,
   * (2) rounding the product of each such calculation to the nearest cent, then (3) summing the rounded results (see calculateTaxFromLineItems)
   */
  taxesInCents: number;

  /**
   * The sum of the subtotal and the tax amount minus the applicationFeeInCents
   */
  publisherAmountInCents: number;

  /**
   * The monetary amount, rounded to the nearest cent, calculated as the subtotal multiplied by the convenience fee percentage,
   * expressed as a decimal, and capped at the specified fee cap, if applicable
   */
  convenienceFeeInCents: number;

  /**
   * A fixed (i.e. independent of other invoice properties) monetary amount, defined in cents on the relevant affidavit reconciliation settings
   */
  automatedAffidavitFeeInCents: number;

  /**
   * The sum of the convenience fee, the automated affidavit fee, and the application fee
   * Does NOT include placement fee as this is due from the demand-side advertiser rather than the publisher
   * and columnAmountInCents is generally an amount owing to Column from the publisher
   * TODO: Rename this property to something like "amountOwedToColumnFromPublisherInCents" to avoid confusion
   */
  columnAmountInCents: number;

  /**
   * The sum of the publisher amount and the Column amount
   */
  totalInCents: number;

  refundAmountInCents: number;

  /**
   * Calculated application fee from organization-level settings
   */
  applicationFeeInCents: number;

  /**
   * The placement fee charged to the demand-side advertiser organization, if applicable
   */
  placementFeeInCents: number;

  /**
   * The total of every notice add-on component. Sits inside the subtotal the advertiser pays but is
   * Column's revenue, so it is also a component of columnAmountInCents.
   */
  noticeAddOnsInCents: number;

  /**
   * The contracted price of a static-priced invoice. Undefined for ordinary invoices.
   */
  staticPriceInCents: number | undefined;

  /**
   * Column Reseller Adjustment Fee charged to the demand-side advertiser (static price − real total).
   * Like placementFeeInCents it is NOT part of columnAmountInCents. 0 for ordinary invoices.
   */
  resellerAdjustmentFeeInCents: number;
} => {
  const { pricing, inAppLineItems, convenienceFeeCap, refund_amount } =
    invoiceSnap.data();

  const {
    publisherAmountInCents,
    totalInCents,
    affidavitFeeInCents: possibleAffidavitFee,
    publisherFeeSplits,
    applicationFeeInCents: possibleApplicationFee,
    placementFeeInCents: possiblePlacementFee,
    staticPriceInCents,
    resellerAdjustmentFeeInCents: possibleResellerAdjustmentFee
  } = pricing;
  const affidavitFeeInCents = possibleAffidavitFee || 0;
  const applicationFeeInCents = possibleApplicationFee || 0;
  const placementFeeInCents = possiblePlacementFee || 0;
  const resellerAdjustmentFeeInCents = possibleResellerAdjustmentFee || 0;
  const noticeAddOnsInCents = sumNoticeAddOnPricing(pricing.noticeAddOns);
  // TODO:
  // - Should this handle distributed fees differently?
  const subtotalInCents = calculateSubtotalFromLineItems(inAppLineItems);
  let { convenienceFeeInCents } = invoiceSnap.data().pricing;

  // Using the negative here because the invoiceType property is not required yet on public notice invoices
  if (!isOrderInvoice(invoiceSnap)) {
    const { convenienceFeePct, convenienceFeeFlat } = invoiceSnap.data();
    const convenienceFeeSubtotalInCents = getConvenienceFeeSubtotalInCents(
      subtotalInCents,
      pricing.noticeAddOns
    );
    const feeAndFeeSplitResult = calculateConvenienceFeeAndFeeSplit(
      convenienceFeeSubtotalInCents,
      {
        convenienceFeeFlat: convenienceFeeFlat || 0,
        convenienceFeePct,
        convenienceFeeCap
      },
      publisherFeeSplits?.convenienceFeeSplitResult?.feeSplit
    );
    convenienceFeeInCents = feeAndFeeSplitResult.convenienceFeeInCents;
  }
  const calculatedColumnAmountInCents =
    affidavitFeeInCents +
    convenienceFeeInCents +
    applicationFeeInCents +
    noticeAddOnsInCents;
  const inAppTaxPct = isOrderInvoice(invoiceSnap)
    ? 0
    : invoiceSnap.data().inAppTaxPct;
  const taxesInCents = calculateTaxFromLineItems(inAppLineItems, inAppTaxPct);
  const calculatedPublisherAmount =
    subtotalInCents +
    taxesInCents -
    applicationFeeInCents -
    noticeAddOnsInCents;

  // Demand-side fees (placement fee, reseller adjustment fee) are subtracted out so
  // columnAmountInCents keeps meaning "fees owed to Column from the publisher".
  const columnAmountInCents = totalInCents
    ? totalInCents -
      calculatedPublisherAmount -
      placementFeeInCents -
      resellerAdjustmentFeeInCents
    : calculatedColumnAmountInCents;

  // Total = Publisher amount + amount publisher owes to Column + amount demand-side partner owes to Column
  //         for placement + reseller adjustment fee
  const calculatedTotalInCents =
    calculatedPublisherAmount +
    columnAmountInCents +
    placementFeeInCents +
    resellerAdjustmentFeeInCents;

  return {
    subtotalInCents,
    taxesInCents,
    publisherAmountInCents,
    convenienceFeeInCents,
    automatedAffidavitFeeInCents: affidavitFeeInCents,
    columnAmountInCents,
    totalInCents: totalInCents || calculatedTotalInCents,
    refundAmountInCents: refund_amount || 0,
    applicationFeeInCents,
    placementFeeInCents,
    noticeAddOnsInCents,
    staticPriceInCents,
    resellerAdjustmentFeeInCents
  };
};

/**
 * Get the rate from a notice, falling back to the newspaper's
 * default liner or default display rate as appropriate.
 */
const getNoticeRateRef = (
  notice: ENotice,
  newspaper: EOrganization
): ERef<ERate> => {
  const { rate, noticeType } = notice;
  if (rate) {
    return rate;
  }

  const defaultRate =
    noticeType === NoticeType.display_ad.value
      ? newspaper.defaultDisplayRate
      : newspaper.defaultLinerRate;

  if (!defaultRate) {
    throw new Error(`Newspaper ${newspaper.name} missing default rates`);
  }

  return defaultRate;
};

const ordinalSuffix = (num: number) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) {
    return 'st';
  }
  if (j === 2 && k !== 12) {
    return 'nd';
  }
  if (j === 3 && k !== 13) {
    return 'rd';
  }
  return 'th';
};

const getRelevantRateDescriptionNoBold = (
  rateRecord: ERate,
  numPublicationDates: number,
  currency: string
) => {
  const rateType = RateType.by_value(rateRecord.rateType);

  if (rateType?.long_description) {
    return rateType.long_description;
  }

  if (rateRecord.runBased) {
    const publishNum = numPublicationDates;
    let ratesDesc = '';
    for (let i = 1; i <= publishNum; i++) {
      ratesDesc += `${currency}${dbToUICurrencyString(
        getApplicableRate(numPublicationDates, rateRecord, i)
      )} / ${
        RateType.by_value(rateRecord.rateType)?.singular
      } for ${i}${ordinalSuffix(i)} run${i !== publishNum ? ', ' : ''}`;
    }
    return ratesDesc;
  }

  return `${currency}${dbToUICurrencyString(
    getApplicableRate(numPublicationDates, rateRecord, 1)
  )} / ${rateType?.singular}`;
};

/**
 * Give a human readable rate description.
 *
 * Ex:
 * '$1.00 / Column Inch'
 */
export const getRelevantRateDescription = (
  rateRecord: ERate,
  numPublicationDates: number,
  currency: string
): string => {
  if (!rateRecord) {
    return '';
  }

  let boldDescription = '';

  if (rateRecord.bold_words) {
    boldDescription = `. Bolded Styling -
    ${currency}${dbToUICurrencyString(rateRecord.bold_words)} / Words.`;
  } else if (rateRecord.line_with_bold_words) {
    boldDescription = `. Bolded Styling -
    ${currency}${dbToUICurrencyString(
      rateRecord.line_with_bold_words
    )} / Line with bolding.`;
  }

  return `${getRelevantRateDescriptionNoBold(
    rateRecord,
    numPublicationDates,
    currency
  )}${boldDescription}`;
};

export const getRelevantRateString = (
  rateRecord: ERate,
  displayParameters: EDisplayParams,
  columns: number
) => {
  const ratePlural = RateType.by_value(rateRecord.rateType)?.plural;
  const relevantParam = relevantDisplayParameterFromRate(
    rateRecord,
    displayParameters,
    columns
  );

  if (
    [RateType.oklahoma.value, RateType.new_oklahoma.value].includes(
      rateRecord.rateType
    )
  )
    return `Tabular Lines: ${oklahoma.getTabularLines(
      displayParameters
    )}, Body Words: ${oklahoma.getBodyWords(displayParameters)}`;

  if (rateRecord.rateType === RateType.single_column_centimetre.value)
    return `Single column centimetres: ${relevantParam}`;

  if (rateRecord.rateType === RateType.per_run.value) return '';
  return rateRecord.rateType === RateType.column_inch.value
    ? `Height: ${roundUp(displayParameters.height, rateRecord.roundOff).toFixed(
        2
      )} / Columns: ${columns}`
    : `${ratePlural}: ${relevantParam ? `${relevantParam}` : 'n/a'}`;
};

const DEFAULT_DISPLAY_PARAMS: EDisplayParams = {
  imgs: [],
  columns: 0,
  words: 0,
  lines: 0,
  area: 0,
  height: 0,
  width: 0,
  headerLines: 0,
  headerWords: 0,
  nonTableBoldedLines: 0,
  boldWords: 0,
  justifications: {
    RIGHT_ALIGN: {
      lines: 0,
      words: 0
    },
    LEFT_ALIGN: {
      lines: 0,
      words: 0
    },
    CENTER_ALIGN: {
      lines: 0,
      words: 0
    },
    LEFT_JUSTIFIED: {
      lines: 0,
      words: 0
    }
  },
  areAnyTablesTooWide: false,
  isTableOverflow: false,
  maxHeightExceeded: false
};

export type DBPricingObj = {
  lineItems: LineItem[];
  // Amount of subtotal in cents
  subtotal: number;
  taxPct: number;
  // Amount of taxes in cents
  taxAmt: number;
  convenienceFeePct: number | null;
  // Amount of convenience fee in cents
  convenienceFee: number | null;
  // convenienceFeeSplit is copied down from the rate at time of notice submission
  convenienceFeeSplit?: FeeSplit;
  // convenienceFeeFlat is copied down from the rate at time of notice submission
  convenienceFeeFlat?: number;
  affidavitFeeInCents?: number;
  total: number;
  // we are setting the convenience fee to null in distributeDbPrice which is leading to incorrect payouts,
  // If we are distributing the fee, we can determine the original fee by accessing the property distributedFee
  distributed?: boolean;
  distributedFee?: number;
  /**
   * @property {NewspaperOrderFeeSplitResults} newspaperOrderFeeSplitResults
   * is used only for newspaper orders where the applicable AdRate has a fee split;
   * this property is used to track the as-applied fee split resulting amount that then gets written on the newspaperOrder
   */
  newspaperOrderFeeSplitResults?: NewspaperOrderFeeSplitResults;

  /**
   * Calculated application fee from organization-level settings
   * It is not added to the total, but is subtracted from the publisherAmountInCents
   * The application fee is independent from the convenience fee, though both are amounts that Column retains
   */
  applicationFeeInCents?: number;

  /**
   * Selected addons for notices e.g. shipping, tracing, wet-sig affidavit
   */
  noticeAddOns?: NoticeAddOnPricing;
};

export type DistributedDBPricingObj = DBPricingObj & {
  convenienceFeeFlat: null;
  convenienceFeePct: null;
  convenienceFee: null;
  distributed: true;
  distributedFee: number;
};

/**
 * Additional fee line items are publisher fees on the organiztion object
 * Fee line items will NOT start with a date string in the description (ex. description: Affidavit Fee)
 * Non-fee line item will start with a date string (ex. 07/01/2022: Custom Notice)
 *
 * @param lineItem
 * @returns boolean
 */
export const isAdditionalFeeLineItem = (lineItem: LineItem): boolean => {
  // Prefer the type field when it exists
  if (typeof lineItem.type === 'number') {
    return (
      lineItem.type === LineItemType.fee.value ||
      // Notice add-on (affidavit/shipping) lines are fees and must classify as such
      lineItem.type === InvoiceItemTypeData.affidavit_fee_oop.value ||
      lineItem.type === InvoiceItemTypeData.shipping_fee_oop.value
    );
  }

  // no description means not custom
  if (!lineItem.description) return false;

  // return false on line items of the format mm/dd/yyyy:
  if (lineItem.description.match(/^\d{2}\/\d{2}\/\d{4}/)) return false;

  return true;
};

export const isPublicationLineItem = (lineItem: LineItem): boolean => {
  if (typeof lineItem.type === 'number') {
    return lineItem.type === LineItemType.publication.value;
  }

  return false;
};

/**
 * Get or infer the LineItemType from a LineItem
 */
export const getLineItemType = (item: LineItem): number => {
  const { type } = item;
  if (typeof type === 'number') {
    return type;
  }

  if (item.singleNoticeId) {
    return LineItemType.bulk_invoice.value;
  }

  return isAdditionalFeeLineItem(item)
    ? LineItemType.fee.value
    : LineItemType.publication.value;
};

/**
 * Retrieves the distribute Fee settings either from the rate or the newspaper respectively
 */
export const getDistributeFeeSettings = (
  newspaper: ESnapshot<EOrganization>,
  rate: ESnapshot<ERate> | undefined
): DistributeSettings | null | undefined => {
  return (
    rate?.data()?.distributeEnoticeFee || newspaper.data()?.distributeEnoticeFee
  );
};

/**
 * Transform the line items of a notice's 'pricing' field into invoice line items
 * which can be edited by the user in the InvoiceForm UI.
 */
export const getInvoiceLineItems = (
  pricing: DBPricingObj,
  activeOrganization: ESnapshotExists<EOrganization>,
  rateSnap: ESnapshotExists<ERate>
): LineItem[] => {
  // Calculate the total additional fees by parsing line items
  let additionalFeeTotal = 0;
  pricing.lineItems.forEach(item => {
    if (!isPublicationLineItem(item)) {
      additionalFeeTotal += item.amount;
    }
  });
  additionalFeeTotal = Math.round(additionalFeeTotal);

  return pricing.lineItems.map((item, i) => {
    const { description, unitPricing } = item;
    const amount = Math.round(item.amount);
    const date = firestoreTimestampOrDateToDate(item.date);
    const type = getLineItemType(item);

    if (
      getDistributeFeeSettings(activeOrganization, rateSnap)?.finalLineItem &&
      rateSnap.data().finalLineItemPricing &&
      rateSnap.data().rateType === RateType.flat.value
    ) {
      return {
        amount:
          i === 0
            ? pricing.subtotal - additionalFeeTotal
            : !isPublicationLineItem(item)
            ? amount
            : 0,
        unitPricing,
        date,
        description,
        type
      };
    }

    if (rateSnap.data().rateType === RateType.flat.value) {
      return {
        amount,
        unitPricing,
        date,
        description,
        type
      };
    }

    return {
      amount,
      unitPricing,
      date,
      description,
      type
    };
  });
};

/**
 * Get all the line items from an invoice representing single publications.
 */
export const getPublicationLineItems = (invoice: ESnapshotExists<EInvoice>) => {
  const { inAppLineItems } = invoice.data();
  return inAppLineItems
    .filter(item => {
      if (item.refund || item.singleNoticeId) {
        return false;
      }

      if (isAdditionalFeeLineItem(item)) {
        return false;
      }

      return item.type === LineItemType.publication.value;
    })
    .sort((a, b) => {
      const aDate = firestoreTimestampOrDateToDate(a.date);
      const bDate = firestoreTimestampOrDateToDate(b.date);
      return aDate.getTime() - bDate.getTime();
    });
};

export const getFeeLineItems = (invoice: ESnapshotExists<EInvoice>) => {
  const { inAppLineItems } = invoice.data();
  return inAppLineItems.filter(item => isAdditionalFeeLineItem(item));
};

/**
 * Find the line item on an invoice that corresponds to publication
 * for a given date.
 */
export const getPublicationLineItemForDate = (
  invoice: ESnapshotExists<EInvoice>,
  publicationDate: Date
) => {
  const publicationLineItems = getPublicationLineItems(invoice);
  const sameDayLineItems = publicationLineItems.filter(item => {
    const itemDate = firestoreTimestampOrDateToDate(item.date);
    return moment(itemDate).isSame(moment(publicationDate), 'day');
  });

  if (sameDayLineItems.length === 0) {
    getErrorReporter().logAndCaptureWarning(
      'Invoice has no line items for day',
      {
        invoiceId: invoice.id,
        publicationDate: publicationDate.toISOString()
      }
    );
    return;
  }

  if (sameDayLineItems.length >= 2) {
    getErrorReporter().logAndCaptureWarning(
      'Invoice has multiple line items for one day',
      {
        invoiceId: invoice.id,
        publicationDate: publicationDate.toISOString()
      }
    );

    return undefined;
  }

  return sameDayLineItems[0];
};

/**
 * Find the the notice publication date corresponding to a given line item on an invoice.
 */
export const getPubDateForPublicationLineItem = (
  lineItem: LineItem,
  publicationDates: FirebaseTimestamp[]
) => {
  const samePublicationDates = publicationDates.filter(pubDate => {
    const lineItemDate = firestoreTimestampOrDateToDate(lineItem.date);
    const pubDateDate = firestoreTimestampOrDateToDate(pubDate);
    return moment(lineItemDate).isSame(moment(pubDateDate), 'day');
  });

  return samePublicationDates[0];
};

/**
 * Find notice pub dates that aren't on a given invoice
 */
export const getNoticePubDatesNotOnInvoice = (
  invoice: ESnapshotExists<EInvoice>,
  publicationDates: FirebaseTimestamp[]
): FirebaseTimestamp[] => {
  if (!publicationDates || publicationDates.length === 0) {
    return [];
  }

  return publicationDates.filter(pubDate => {
    const pubDateAsDate = firestoreTimestampOrDateToDate(pubDate);
    return !getPublicationLineItemForDate(invoice, pubDateAsDate);
  });
};

/**
 * Find the difference in run dates
 */
export const getPubDateAndPublicationLineItemsDifference = (
  invoice: ESnapshotExists<EInvoice>,
  publicationDates: FirebaseTimestamp[]
) => {
  const pubDateDiffs: FirebaseTimestamp[] = getNoticePubDatesNotOnInvoice(
    invoice,
    publicationDates
  );

  const publicationLineItemDiffs: LineItem[] = getPublicationLineItems(
    invoice
  ).filter(lineItem => {
    return !getPubDateForPublicationLineItem(lineItem, publicationDates);
  });

  return { pubDateDiffs, publicationLineItemDiffs };
};

/**
 * Get the invoiced price for a single run of a notice.
 *
 * Note that this does not attempt to reverse any fee-embedding, so
 * when calling this function be sure to understand the newspaper context!
 */
export const getSingleRunPriceFromInvoice = (
  notice: ESnapshotExists<ENotice>,
  invoice: ESnapshotExists<EInvoice>,
  runNumber: number
) => {
  const { publicationDates } = notice.data();
  if (!publicationDates || publicationDates.length <= runNumber) {
    throw new Error(
      `Notice ${notice.id} does not have ${runNumber} publication dates!`
    );
  }

  const publicationDate = firestoreTimestampOrDateToDate(
    publicationDates[runNumber]
  );

  const matchingLineItem = getPublicationLineItemForDate(
    invoice,
    publicationDate
  );
  if (!matchingLineItem) {
    return undefined;
  }

  return matchingLineItem.amount;
};

/**
 * Get the column rep fee percentage specified in a newspaper's additional fees.
 */
const getColumnRepFeePctFromNewspaper = (
  convenienceFee: ConvenienceFee,
  newspaper: Pick<EOrganization, 'additionalFees'>
): number => {
  const { additionalFees } = newspaper;
  const { convenienceFeePct } = convenienceFee;
  const columnRepFeeOnNewspaper = additionalFees?.find(
    af => af.description === COLUMN_REP_FEE
  );

  // We charge an additional fee if all of the below are true:
  //  - The newspaper has a COLUMN_REP_FEE
  //  - The existing convenience fee percentage is > 0
  //  - A column rep had contact with the notice in a meaningful way
  const columnRepPercentage =
    !!columnRepFeeOnNewspaper &&
    isPercentAdditionalFee(columnRepFeeOnNewspaper) &&
    convenienceFeePct > 0
      ? columnRepFeeOnNewspaper.feePercentage
      : 0;

  return columnRepPercentage;
};

type NoticeProductData = Pick<
  ENotice,
  'fixedPrice' | 'publicationDates' | 'columns' | 'noticeType'
>;

type OrderProductData = Pick<NewspaperOrder, 'colorOptions'> &
  Pick<NewspaperOrder, 'jobcaseSettings'> &
  Pick<Ad, 'orderImages'> &
  Pick<ENotice, 'publicationDates'> & { usesTextColor: boolean };

export type PricingParameters = NoticeProductData | OrderProductData;

export const isNoticePricingParameters = (
  pricingParameters: PricingParameters
): pricingParameters is NoticeProductData => 'columns' in pricingParameters;

export const getAdditionalFeesForPublicationLineItems = (
  lineItems: LineItem[],
  rate: AdRate,
  newspaper: EOrganization,
  pricingParameters: PricingParameters,
  affidavitData: AffidavitPricingData,
  lastPubDatePlusOneMinute: Date
): LineItem[] => {
  let runningFeeLineItems: LineItem[] = [];
  const rateAdditionalFees = getAdditionalFeesFromRate(rate);

  const affidavitAdditionalFeeLineItems =
    getAffidavitAdditionalFeeLineItemsFromRate(
      rateAdditionalFees,
      affidavitData.mail || [],
      lastPubDatePlusOneMinute
    );
  runningFeeLineItems = runningFeeLineItems.concat(
    affidavitAdditionalFeeLineItems
  );

  // allow for additional fees for custom affidavits
  // this enables additional fees for Ogden papers that
  // are requesting notice-type-specific pricing
  if (newspaper.customAffidavitFee) {
    const customAffidavitFeeItem: LineItem = {
      date: lastPubDatePlusOneMinute,
      amount: newspaper.customAffidavitFee,
      description: 'Custom Affidavit Fee',
      type: LineItemType.fee.value
    };
    runningFeeLineItems = runningFeeLineItems.concat([customAffidavitFeeItem]);
  }

  const { enableFixedPriceAdditionalFees } = newspaper;
  const nonAffidavitAdditionalFees =
    getNonAffidavitAdditionalFeeLineItemsFromAdditionalFees(
      rate.product,
      rateAdditionalFees,
      lastPubDatePlusOneMinute,
      lineItems,
      pricingParameters,
      enableFixedPriceAdditionalFees
    );

  runningFeeLineItems = runningFeeLineItems.concat(nonAffidavitAdditionalFees);

  if (isOrderRate(rate) && rate.colorFees) {
    const colorAdditionalFees = getColorAdditionalFeeLineItems(
      rate.colorFees,
      pricingParameters,
      lastPubDatePlusOneMinute
    );
    runningFeeLineItems = runningFeeLineItems.concat(colorAdditionalFees);
  }

  if (isOrderRate(rate) && rate.jobcaseFee) {
    const jobcaseAdditionalFee = getJobcaseAdditionalFeeLineItem(
      rate.jobcaseFee,
      pricingParameters,
      lastPubDatePlusOneMinute
    );
    if (jobcaseAdditionalFee) {
      runningFeeLineItems = runningFeeLineItems.concat(jobcaseAdditionalFee);
    }
  }
  /**
   * These cross-paper fees should be calculated after all other fees to ensure any
   * percentage-based fees are calculated against a total including all other fees
   */
  if (newspaper.additionalFees) {
    const lineItemsWithCurrentFees = lineItems.concat(runningFeeLineItems);
    const paperFeeLineItems =
      getNonAffidavitAdditionalFeeLineItemsFromAdditionalFees(
        rate.product,
        newspaper.additionalFees,
        lastPubDatePlusOneMinute,
        lineItemsWithCurrentFees,
        pricingParameters,
        enableFixedPriceAdditionalFees
      );
    runningFeeLineItems = runningFeeLineItems.concat(paperFeeLineItems);
  }

  return runningFeeLineItems;
};

function getTaxRatePercent({
  forProduct,
  forPublisher
}: {
  forProduct: Product;
  forPublisher: EOrganization;
}): EOrganization['taxPct'] {
  const { productTaxSettings, taxPct = 0 } = forPublisher;

  if (!productTaxSettings) {
    return forProduct === Product.Notice ? taxPct : 0;
  }

  return productTaxSettings[forProduct] ? taxPct : 0;
}

/**
 * Calculate notice pricing based on the intrinsic notice properties
 * and the line items for each publication.
 */
export const createDBPricingObjectFromDataAndPublicationLineItems = (
  productType: Product,
  pricingParameters: PricingParameters,
  newspaper: EOrganization,
  rate: AdRate,
  lineItems: LineItem[],
  affidavitData: AffidavitPricingData,
  discountConfig: DiscountConfig | undefined,
  options: {
    // In the case of coupons, we want the coupon to have precedent over the minimum to allow for free orders
    blockMinimums?: boolean;
    enableFeeSplitsForOrders?: boolean;
    // If true, does not regenerate additional fee line items as they are already included in the line items
    lineItemsIncludeAdditionalFees?: boolean;
    enableNewOklahomaRate?: boolean;
  }
): DBPricingObj => {
  const { publicationDates } = pricingParameters;
  const lastIndex = publicationDates.length - 1;
  const lastPubDatePlusOneMinute = moment(publicationDates[lastIndex].toDate())
    .add(1, 'm')
    .toDate();

  let newLineItems = lineItems;

  if (discountConfig) {
    newLineItems = getUpdatedDiscountLineItemsForNotice(
      discountConfig,
      newLineItems
    );
  }

  if (!options.lineItemsIncludeAdditionalFees) {
    const additionalFeeLineItems = getAdditionalFeesForPublicationLineItems(
      newLineItems,
      rate,
      newspaper,
      pricingParameters,
      affidavitData,
      lastPubDatePlusOneMinute
    );
    newLineItems = newLineItems.concat(additionalFeeLineItems);
  }

  const addOnPricingContext = getAddOnPricingContextForNotice(
    affidavitData,
    affidavitData.mail ?? []
  );
  const [addOnErr, noticeAddOns] = computeNoticeAddOnPricing(
    affidavitData.mail ?? [],
    affidavitData.noticeAddOnQuote?.noticeAddOnPricing ??
      newspaper.noticeAddOnPricing ??
      {},
    addOnPricingContext
  );
  if (addOnErr) {
    getErrorReporter().logAndCaptureCriticalError(
      ColumnService.PAYMENTS,
      addOnErr,
      'Failed to compute notice add-on pricing; skipping add-ons',
      {}
    );
    throw addOnErr;
  } else if (noticeAddOns && Object.keys(noticeAddOns).length > 0) {
    const addOnLineItems = expandNoticeAddOnsToLineItems(
      noticeAddOns,
      lastPubDatePlusOneMinute
    );
    newLineItems = newLineItems.concat(addOnLineItems);
  }

  const taxPct = getTaxRatePercent({
    forProduct: productType,
    forPublisher: newspaper
  });

  const totalAcrossRuns = newLineItems.reduce(
    (acc, lineItem) => acc + lineItem.amount,
    0
  );

  const rateMinimum = (options?.blockMinimums ? 0 : rate.minimum) ?? 0;
  const subtotal = Math.floor(Math.max(totalAcrossRuns, rateMinimum));

  const lastNonFeeNonDiscountLineItemIndex =
    getLastNonFeeAndNonDiscountLineItemIndex(newLineItems);

  // enforce that all line items are cents
  let roundedSubtotal = 0;
  for (const item of newLineItems) {
    const centTotal = Math.floor(item.amount);
    item.amount = centTotal;
    roundedSubtotal += centTotal;
  }

  // put the impact of rounding onto the last non-fee line item
  newLineItems[lastNonFeeNonDiscountLineItemIndex].amount +=
    subtotal - roundedSubtotal;

  let additionalFeeTotal = 0;
  let changedFinalLineItem = false;
  if (isNoticeRate(rate) && rate.finalLineItemPricing) {
    for (let i = newLineItems.length - 1; i >= 0; i -= 1) {
      if (!isPublicationLineItem(newLineItems[i])) {
        additionalFeeTotal += newLineItems[i].amount;
      } else if (changedFinalLineItem) {
        newLineItems[i].amount = 0;
      } else {
        newLineItems[i].amount = totalAcrossRuns - additionalFeeTotal;
        changedFinalLineItem = true;
      }
    }
  }

  const taxAmt = calculateTaxFromLineItems(newLineItems, taxPct);

  const convenienceFeeFromRate = rateToConvenienceFee(rate);
  const columnRepFeePctFromNewspaper = getColumnRepFeePctFromNewspaper(
    convenienceFeeFromRate,
    newspaper
  );

  const combinedConvenienceFeePct =
    convenienceFeeFromRate.convenienceFeePct + columnRepFeePctFromNewspaper;

  if (combinedConvenienceFeePct > convenienceFeeFromRate.convenienceFeePct) {
    getErrorReporter().logInfo(
      'Column rep fee from newspaper added to convenience fee',
      {
        newspaperName: newspaper.name,
        oldConvenienceFeePct: `${convenienceFeeFromRate.convenienceFeePct}`,
        newConvenienceFeePct: `${combinedConvenienceFeePct}`
      }
    );
  }

  const convenienceFee = {
    convenienceFeeFlat: convenienceFeeFromRate.convenienceFeeFlat,
    // NOTE: this value for convenienceFee.convenienceFeePct
    // is not further adjusted for the actual convenienceFeeInCents valueFee below
    // (viz., as the value may be adjusted by a convenience fee cap or any feeSplits).
    convenienceFeePct: combinedConvenienceFeePct,
    convenienceFeeCap: convenienceFeeFromRate.convenienceFeeCap
  };

  const convenienceFeeSplit = getConvenienceFeeSplitFromRate(rate, {
    enableFeeSplitsForOrders: options.enableFeeSplitsForOrders ?? false
  });

  const convenienceFeeSubtotal = getConvenienceFeeSubtotalInCents(
    subtotal,
    noticeAddOns
  );

  const { convenienceFeeInCents, trackedConvenienceFeeSplitResult } =
    calculateConvenienceFeeAndFeeSplit(
      convenienceFeeSubtotal,
      convenienceFee,
      convenienceFeeSplit
    );

  // This is the only time we should call this function as the notice data has not yet been set. Otherwise, use `getAffidavitFeeInCentsForNotice`.
  // Fee splits with respect to the Column affidavit fee (stored on ARS and inherited using the usual hierarchy) will be applied here.
  const defaultAffidavitFeeInCents = getAffidavitFeeInCentsFromSettingsAndMail(
    affidavitData.settings,
    affidavitData.mail ?? []
  );

  const affidavitFeeInCents = noticeAddOnCoversAffidavit(
    noticeAddOns,
    addOnPricingContext
  )
    ? 0
    : getFinalAffidavitFeeInCents({
        subtotalInCents: subtotal,
        defaultAffidavitFeeInCents,
        convenienceFee,
        affidavitSettings: affidavitData.settings,
        requiresAffidavit: affidavitData.requiresAffidavit,
        publicationDates: pricingParameters.publicationDates,
        rate
      });

  const total = subtotal + taxAmt + convenienceFeeInCents + affidavitFeeInCents;

  const shouldReturnNewspaperOrderFeeSplitResults =
    options.enableFeeSplitsForOrders &&
    trackedConvenienceFeeSplitResult &&
    isOrderRate(rate);

  const applicationFeeInCents = calculateApplicationFeeInCents(
    newspaper.applicationFeeSettings,
    subtotal
  );
  return {
    lineItems: newLineItems,
    subtotal,
    taxPct: floatToDBPercent(taxPct),
    taxAmt,
    // note -- below convenienceFeePct value is not adjusted for convenience fee caps or feeSplits
    convenienceFeePct: floatToDBPercent(convenienceFee.convenienceFeePct),
    convenienceFee: convenienceFeeInCents,
    convenienceFeeFlat: convenienceFee.convenienceFeeFlat,
    ...(convenienceFeeSplit && { convenienceFeeSplit }),
    ...(affidavitFeeInCents && { affidavitFeeInCents }),
    total,
    ...(shouldReturnNewspaperOrderFeeSplitResults && {
      newspaperOrderFeeSplitResults: {
        convenienceFeeSplitResult: trackedConvenienceFeeSplitResult
      }
    }),
    applicationFeeInCents,
    ...(noticeAddOns && { noticeAddOns })
  };
};

/**
 * Add-on pricing reads the quote frozen when the filer was shown it, never the
 * publisher's current configuration: a paper moving between wet signature and RON
 * after placement would otherwise change what the notice bills, silently and in
 * either direction.
 *
 * With no quote, pricing falls back to live configuration — automated placement
 * must not break on a missing one. Notices placed before quotes were written have
 * none and are not being backfilled, so this is logged rather than alerted on.
 */
export const getAddOnPricingContextForNotice = (
  affidavitData: Pick<
    AffidavitPricingData,
    'noticeAddOnQuote' | 'paperLevelSettings' | 'settings'
  >,
  mail: MailDelivery[]
): NoticeAddOnPricingContext => {
  if (affidavitData.noticeAddOnQuote) {
    return getContextFromNoticeAddOnQuote(affidavitData.noticeAddOnQuote);
  }

  if (hasBillableAddOnMail(mail)) {
    getErrorReporter().logWarn(
      'Notice has no frozen add-on quote, so add-ons will be priced from live configuration'
    );
  }

  return deriveNoticeAddOnPricingContext(
    affidavitData.paperLevelSettings ?? affidavitData.settings
  );
};

export type AdDataForPricing = Pick<
  ENotice,
  'noticeType' | 'publicationDates' | 'columns' | 'fixedPrice'
>;

export const getDescriptionForLineItem = (
  date: FirebaseTimestamp,
  newspaper: EOrganization,
  rate: AdRate
): string | undefined => {
  const { dayRate, dayEnum } = findDayRate(rate, date);
  const formattedDate = moment(date.toDate()).format('M/D/YYYY');

  if (dayRate) {
    const dayLabel = Day.by_value(dayEnum)?.label || '';
    return `${formattedDate}: Custom notice (${dayLabel} Rate)`;
  }

  if (
    rate.product === Product.Obituary ||
    rate.product === Product.Classified
  ) {
    const productName = PRODUCT_TO_NAME[rate.product].singular;

    return `${newspaper.name} ${formattedDate} ${productName}`;
  }
  return undefined;
};

/**
 * This is the one true pre-invoice notice pricing function!
 * Final pricing can only be determined after invoicing: see calculateInvoicePricing()
 */
export const createDBPricingObjectFromData = (
  productType: Product,
  adDataForPricing: PricingParameters,
  newspaper: EOrganization,
  rate: AdRate,
  displayParams: DisplayParams | undefined,
  affidavitPricingData: AffidavitPricingData,
  discountConfig: DiscountConfig | undefined,
  createConfig: {
    enablePeriodBasedOrderPricing: boolean;
    enableFeeSplitsForOrders: boolean;
    enableNewOklahomaRate: boolean;
  }
): DBPricingObj => {
  const displayParameters = displayParams ?? DEFAULT_DISPLAY_PARAMS;

  const { publicationDates } = adDataForPricing;

  let publicationLineItems: LineItem[] = [];

  const {
    enablePeriodBasedOrderPricing,
    enableFeeSplitsForOrders,
    enableNewOklahomaRate
  } = createConfig;

  if (enablePeriodBasedOrderPricing) {
    publicationLineItems = getPublicationLineItemsForPeriodBasedRate(
      publicationDates,
      rate,
      newspaper,
      adDataForPricing,
      displayParameters,
      createConfig
    );
  } else {
    publicationLineItems = publicationDates.map((date, i) => {
      const amount = calculateSingleRunPrice(
        adDataForPricing,
        displayParameters,
        newspaper,
        rate,
        i,
        { enableNewOklahomaRate }
      );
      const description = getDescriptionForLineItem(date, newspaper, rate);
      return {
        date: date.toDate(),
        amount,
        ...(description && { description }),
        type: LineItemType.publication.value
      };
    });
  }

  return createDBPricingObjectFromDataAndPublicationLineItems(
    productType,
    adDataForPricing,
    newspaper,
    rate,
    publicationLineItems,
    affidavitPricingData,
    discountConfig,
    { enableFeeSplitsForOrders, enableNewOklahomaRate }
  );
};

/**
 * Price a notice, allowing overrides of the displayParams and the rate. Useful
 * in places like the placement flow where those fields are dynamic.
 */
export const createDBPricingObject = async (
  ctx: EFirebaseContext,
  noticeSnap: ESnapshotExists<ENotice>,
  displayParams: EDisplayParams | undefined,
  rateOverride: ERef<ERate> | undefined
): Promise<DBPricingObj> => {
  if (!exists(noticeSnap)) {
    throw new Error('Notice not set');
  }

  const displayParameters = displayParams ?? DEFAULT_DISPLAY_PARAMS;

  const notice = noticeSnap.data();

  const newspaperSnap = await getOrThrow(noticeSnap.data().newspaper);
  const newspaper = newspaperSnap.data();

  const rateRef = rateOverride ?? getNoticeRateRef(notice, newspaper);
  const rate = (await getOrThrow(rateRef)).data();

  const affidavitPricingData = await getAffidavitPricingData(ctx, noticeSnap);

  const discountConfig = await getActiveDiscountConfigForNotice(
    ctx,
    noticeSnap.data()
  );

  const enablePeriodBasedOrderPricing =
    await getLaunchDarklyContext().getBooleanFeatureFlag(
      LaunchDarklyFlags.ENABLE_PERIOD_BASED_ORDER_PRICING,
      { type: 'anonymous', defaultValue: false }
    );

  const enableFeeSplitsForOrders =
    await getLaunchDarklyContext().getBooleanFeatureFlag(
      LaunchDarklyFlags.ENABLE_FEE_SPLITS_FOR_ORDERS,
      { type: 'anonymous', defaultValue: false }
    );

  const enableNewOklahomaRate =
    await getLaunchDarklyContext().getBooleanFeatureFlag(
      LaunchDarklyFlags.ENABLE_NEW_OKLAHOMA_RATE,
      { type: 'anonymous', defaultValue: false }
    );

  return createDBPricingObjectFromData(
    Product.Notice,
    notice,
    newspaper,
    rate,
    displayParameters,
    affidavitPricingData,
    discountConfig,
    {
      enablePeriodBasedOrderPricing,
      enableFeeSplitsForOrders,
      enableNewOklahomaRate
    }
  );
};

/**
 * Price a notice using all its internal data (display params, rate, etc)
 * See: createDBPricingObject
 */
export const createDBPricingFromNotice = async (
  ctx: EFirebaseContext,
  noticeSnap: ESnapshotExists<ENotice>
) => {
  const { displayParams } = noticeSnap.data();
  return createDBPricingObject(
    ctx,
    noticeSnap,
    displayParams,
    /* rateOverride= */ undefined
  );
};

/**
 * Sum the convenience fee and the affidavit fee.
 */
export const getCombinedColumnFeeInCentsFromDbPricingObj = (
  dbPricingObj: DBPricingObj
): number => {
  return (
    (dbPricingObj.convenienceFee ?? 0) + (dbPricingObj.affidavitFeeInCents ?? 0)
  );
};

/**
 * Checks if one of the distribute settings is turned on, other wise it returns false
 */
export const shouldDistributeFee = (
  distributeFeeSettings: DistributeSettings | null | undefined
) => {
  return (
    !!distributeFeeSettings &&
    Object.values(distributeFeeSettings).some(setting => setting)
  );
};

export const getAffidavitFeeIndexFromDBPricingObjLineItems = (
  lineItems: LineItem[]
) => {
  return lineItems.findIndex(
    ({ description = '', type }) =>
      fuzzyStringContains(description, 'affidavit') &&
      type === LineItemType.fee.value
  );
};

const distributeDbPrice = (
  dbPricingObj: DBPricingObj,
  distributeEnoticeFee: DistributeSettings | undefined,
  rate: Pick<ERate, 'finalLineItemPricing' | 'rateType'> | undefined
): DBPricingObj | DistributedDBPricingObj => {
  const shouldDistributeConvenienceFee =
    !!distributeEnoticeFee?.evenly || !!distributeEnoticeFee?.finalLineItem;

  const affidavitFeeIndex = getAffidavitFeeIndexFromDBPricingObjLineItems(
    dbPricingObj.lineItems
  );
  const shouldDistributeAffidaviteFee =
    // Distribution settings are set to distribute the affidavit fee
    !!distributeEnoticeFee?.affidavitFee &&
    // The pricing object has a column affidavit fee to embed
    !!dbPricingObj.affidavitFeeInCents &&
    // There is a publisher fee line item into which we can embed the affidavit fee
    affidavitFeeIndex !== -1;

  const combinedFee = getCombinedColumnFeeInCentsFromDbPricingObj(dbPricingObj);
  const feeToDistribute = shouldDistributeAffidaviteFee
    ? dbPricingObj.convenienceFee ?? 0
    : combinedFee;

  const taxAmt = calculateTaxFromLineItems(
    dbPricingObj.lineItems,
    dbPricingObj.taxPct
  );

  const { total } = dbPricingObj;
  let { subtotal } = dbPricingObj;
  let distributedFee = 0;

  // If we're distributing the convenience fee, then it should be included in the subtotal
  if (shouldDistributeConvenienceFee) {
    subtotal += dbPricingObj.convenienceFee ?? 0;
    distributedFee += dbPricingObj.convenienceFee ?? 0;
  }

  // If we're distributing the affidavit fee (separately or as part of the convenience fee),
  // then it should be included in the subtotal
  if (shouldDistributeAffidaviteFee || shouldDistributeConvenienceFee) {
    subtotal += dbPricingObj.affidavitFeeInCents ?? 0;
    distributedFee += dbPricingObj.affidavitFeeInCents ?? 0;
  }

  const baseLineItems =
    shouldDistributeConvenienceFee && rate
      ? getDistributedLineItems(
          dbPricingObj.lineItems,
          distributeEnoticeFee,
          rate,
          {
            feeToDistribute,
            expectedSubtotal: subtotal
          }
        )
      : dbPricingObj.lineItems;

  const lineItems = baseLineItems.map((item, idx) => {
    if (!shouldDistributeAffidaviteFee || idx !== affidavitFeeIndex) {
      return item;
    }

    const { amount: publisherAffidavitFee, unitPricing } = item;
    const columnAffidavitFee = dbPricingObj.affidavitFeeInCents ?? 0;

    const amount = publisherAffidavitFee + columnAffidavitFee;

    if (!unitPricing) {
      return { ...item, amount, unitPricing: { price: amount, quantity: 1 } };
    }

    const { quantity } = unitPricing;

    const newUnitPricing = {
      price: amount / quantity,
      quantity
    };

    return { ...item, amount, unitPricing: newUnitPricing };
  });

  return {
    ...dbPricingObj,
    lineItems,
    affidavitFeeInCents: 0,
    convenienceFee: shouldDistributeConvenienceFee
      ? null
      : dbPricingObj.convenienceFee,
    convenienceFeePct: shouldDistributeConvenienceFee
      ? null
      : dbPricingObj.convenienceFeePct,
    subtotal,
    taxAmt,
    total,
    distributed: true,
    distributedFee
  };
};

export const maybeDistributeDbPrice = (
  dbPricingObj: DBPricingObj,
  distributeEnoticeFee: DistributeSettings | undefined,
  rate: Pick<ERate, 'finalLineItemPricing' | 'rateType'> | undefined
): DBPricingObj | DistributedDBPricingObj => {
  if (!shouldDistributeFee(distributeEnoticeFee) || dbPricingObj.distributed) {
    return dbPricingObj;
  }

  return distributeDbPrice(dbPricingObj, distributeEnoticeFee, rate);
};

export const invoiceDataToDBPricingObject = (
  inAppLineItems: LineItem[],
  convenienceFeePct: number,
  convenienceFeeFlat: number,
  convenienceFeeCap: number | undefined,
  optionalAffidavitFeeInCents: number | undefined,
  inAppTaxPct: number,
  distributeEnoticeFee?: DistributeSettings | null,
  rate?: Pick<
    ERate,
    'finalLineItemPricing' | 'rateType' | 'convenienceFeeSplit'
  >,
  appliedBalance?: number,
  placementFeeInCents?: number,
  noticeAddOns?: NoticeAddOnPricing,
  resellerAdjustmentFeeInCents?: number
): DBPricingObj | DistributedDBPricingObj => {
  const subtotal = inAppLineItems
    .map(item => item.amount)
    .reduce((pre, cur) => pre + cur, 0);

  const { convenienceFeeSplit } = rate || {};
  const convenienceFeeSubtotal = getConvenienceFeeSubtotalInCents(
    subtotal,
    noticeAddOns
  );
  const convenienceFee = calculateConvenienceFee(
    convenienceFeeSubtotal,
    {
      convenienceFeeFlat,
      convenienceFeePct,
      convenienceFeeCap
    },
    convenienceFeeSplit
  );

  const affidavitFeeInCents = optionalAffidavitFeeInCents || 0;

  const taxAmt = calculateTaxFromLineItems(inAppLineItems, inAppTaxPct);

  const total =
    subtotal +
    taxAmt +
    convenienceFee +
    affidavitFeeInCents -
    (appliedBalance || 0);

  let obj: DBPricingObj = {
    lineItems: inAppLineItems
      .map(item => {
        // TODO(COREDEV-1031): This brings in extra properties from LineItem
        // which are not on the LineItem type!
        return {
          ...item,
          type: getLineItemType(item),
          date: firestoreTimestampOrDateToDate(item.date)
        };
      })
      .sort(
        (a: LineItem, b: LineItem) =>
          (a.date as Date).getTime() - (b.date as Date).getTime()
      ),
    taxPct: inAppTaxPct,
    taxAmt,
    convenienceFeePct,
    convenienceFee,
    affidavitFeeInCents,
    subtotal,
    total
  };

  // Distribute fees (if applicable)
  obj = maybeDistributeDbPrice(obj, distributeEnoticeFee ?? undefined, rate);

  /**
   * Always embed the demand-side fees (placement fee, reseller adjustment fee) proportionally across
   * publication line items, regardless of the rate's distribution settings, so no fee line is
   * visible and no single low-cost date balloons.
   * https://columnpbc.slack.com/archives/C083848LSJK/p1777574801562429?thread_ts=1777317188.627079&cid=C083848LSJK
   */
  const demandSideFeesInCents =
    (placementFeeInCents || 0) + (resellerAdjustmentFeeInCents || 0);
  if (demandSideFeesInCents) {
    const pubItems = obj.lineItems.filter(li => isPublicationLineItem(li));
    const distributedPubItems = distributeFeeProportionally(
      pubItems,
      demandSideFeesInCents
    );
    let pubIndex = 0;
    obj = {
      ...obj,
      lineItems: obj.lineItems.map(li => {
        if (!isPublicationLineItem(li)) {
          return li;
        }
        return distributedPubItems[pubIndex++];
      }),
      subtotal: obj.subtotal + demandSideFeesInCents,
      total: obj.total + demandSideFeesInCents
    };
  }

  return obj;
};

export const invoiceToDBPricingObject = ({
  invoiceSnap,
  rateSnap,
  newspaperSnap
}: {
  invoiceSnap: ESnapshotExists<EInvoice>;
  rateSnap: ESnapshot<ERate>;
  newspaperSnap: ESnapshotExists<EOrganization>;
}): DBPricingObj => {
  const {
    inAppLineItems,
    convenienceFeePct,
    convenienceFeeFlat = 0,
    convenienceFeeCap,
    pricing: {
      affidavitFeeInCents,
      placementFeeInCents,
      noticeAddOns,
      resellerAdjustmentFeeInCents
    },
    inAppTaxPct
  } = invoiceSnap.data();

  return invoiceDataToDBPricingObject(
    inAppLineItems,
    convenienceFeePct,
    convenienceFeeFlat,
    convenienceFeeCap,
    affidavitFeeInCents,
    inAppTaxPct,
    getDistributeFeeSettings(newspaperSnap, rateSnap),
    rateSnap.data(),
    undefined,
    placementFeeInCents,
    noticeAddOns,
    resellerAdjustmentFeeInCents
  );
};

export type UILineItem = {
  date: Date | FirebaseTimestamp;
  description: string | null;
  amount: string;
};

export const maybeGetColumnRepFeePctFromNotice = async (
  notice: ESnapshotExists<ENotice>,
  rate: ESnapshotExists<ERate>
) => {
  const ratePct = rate.data().columnRepFeePct;
  if (typeof ratePct === 'number') {
    return ratePct;
  }

  const newspaper = await getOrThrow(notice.data().newspaper);
  return maybeGetColumnRepFeePctFromNewspaper(newspaper);
};

/**
 * Calculate the advertiser and publisher Column Reps fees for a notice.
 * Rep fee can be on the rate or newspaper. Rep fees on rates override rep fee on the newspaper
 */
export const calculateColumnRepsFee = async (
  notice: ESnapshotExists<ENotice>,
  invoice: ESnapshotExists<EInvoice>
): Promise<{ advertiserFeeInCents: number; publisherFeeInCents: number }> => {
  const noFees = {
    advertiserFeeInCents: 0,
    publisherFeeInCents: 0
  };

  const rate = await getOrThrow(notice.data().rate);
  const columnRepFeePctFromNotice = await maybeGetColumnRepFeePctFromNotice(
    notice,
    rate
  );
  const percentage = columnRepFeePctFromNotice;

  if (!(percentage && percentage > 0)) {
    return noFees;
  }

  const { subtotalInCents, convenienceFeeInCents } =
    getInvoiceAmountsBreakdown(invoice);

  // If the advertiser paid any convenience fee, it should have included the Column Rep fee
  if (convenienceFeeInCents > 0) {
    return {
      advertiserFeeInCents: convenienceFeeInCents,
      publisherFeeInCents: 0
    };
  }

  // The rep fee is part of the convenience fee family, so it uses the same base.
  const convenienceFeeSubtotalInCents = getConvenienceFeeSubtotalInCents(
    subtotalInCents,
    invoice.data().pricing.noticeAddOns
  );

  // Otherwise, the publisher has to pay the column rep fee
  const repFeeInCents = calculateConvenienceFee(
    convenienceFeeSubtotalInCents,
    {
      convenienceFeeFlat: 0,
      convenienceFeePct: percentage,
      convenienceFeeCap: undefined
    },
    undefined // no feeSplit on rep fee
  );

  return {
    publisherFeeInCents: repFeeInCents,
    advertiserFeeInCents: 0
  };
};

/**
 * Calculate the fee that should be paid by the publisher based on async design work.
 */
export const calculateAsyncDesignFee = async (
  ctx: EFirebaseContext,
  notice: ESnapshotExists<ENotice>,
  invoice: ESnapshotExists<EInvoice>
): Promise<number> => {
  const config = await getInheritedProperty(
    notice.data().newspaper,
    'asyncDesignPricing'
  );

  if (!config) {
    return 0;
  }

  // Check the events log for the notice. If the notice has any matching events
  // it is eligible for a fee. If there are multiple matches we look for the
  // latest one.
  const events = await ctx
    .eventsRef<NoticeAsyncDesignFinalizedEvent>()
    .where('type', '==', NOTICE_ASYNC_DESIGN_FINALIZED)
    .where('notice', '==', notice.ref)
    .orderBy('createdAt', 'desc')
    .limit(1)
    .get();

  if (events.docs.length === 0) {
    return 0;
  }

  if (config.type === 'percent') {
    const { subtotalInCents } = getInvoiceAmountsBreakdown(invoice);
    const feeInCents = Math.round((subtotalInCents * config.subtotalPct) / 100);

    const pages = events.docs[0].data().data.pages ?? 1;
    const perPageCap = config.cap?.perPage
      ? config.cap.perPage * pages
      : Number.MAX_SAFE_INTEGER;
    const totalCap = config.cap?.total ?? Number.MAX_SAFE_INTEGER;

    return Math.min(feeInCents, perPageCap, totalCap);
  }

  if (config.type === 'sized') {
    const modularSize = await notice.data().modularSize?.get();

    const price = modularSize?.data()?.designFeeInCents || 0;

    return price;
  }

  return 0;
};

type InvoicePricingAmounts = {
  applicationFeeInCents: number;
  publisherAmountInCents: number;
  totalInCents: number;
  subtotalInCents: number;
  convenienceFeeInCents: number;
  affidavitFeeInCents: number;
  taxInCents: number;
  /** Additive placement fee in cents for demand-side billing */
  placementFeeInCents?: number;
  /** Contracted price of a static-priced notice; only set when pricing was static. */
  staticPriceInCents?: number;
  /** Column Reseller Adjustment Fee (static price − real total), only when positive. */
  resellerAdjustmentFeeInCents?: number;
  publisherFeeSplits?: {
    convenienceFeeSplitResult?: TrackedFeeSplitResult;
    autoAffidavitFeeSplitResult?: TrackedFeeSplitResult;
  };
  noticeAddOns?: NoticeAddOnPricing;
};

/**
 * Calculating the total is tricky, because the data.publisherAmountInCents is meant to reflect what we
 * intend to send to the publisher, and thus includes both the sum of all line items (subtotal)
 * plus any applicable tax (subtotal * tax percent). However, we need to calculate our convenience
 * fee from the original subtotal.
 *
 * The steps to calculate the proper total are:
 * - Recalculate the subtotal from all line items.
 * - Apply any discounts to the subtotal.
 * - Calculate the tax amount as a percentage of the subtotal.
 * - Calculate our convenience fee as a percentage of the subtotal.
 * - Calculate our application fee from the subtotal.
 * - Sum all of the above (plus any affidavit notarization fee) to get the total
 * - Remove the application fee from the subtotal + tax to get the publisher amount (i.e. what will be transfered)
 */
export const calculateInvoicePricing = (
  newspaper: ESnapshotExists<EOrganization>,
  data: {
    advertiserOrganization: ESnapshotExists<EOrganization> | null;
    affidavitReconciliationSettings:
      | AffidavitReconciliationSettings
      | undefined;
    /**
     * `affidavitReconciliationSettings` resolved without the notice-level
     * override. Used only to decide whether the *paper* requires wet sig, which
     * governs the "publisher covers the first copy" add-on waiver. See
     * `AffidavitPricingData.paperLevelSettings`.
     */
    paperLevelAffidavitReconciliationSettings?:
      | AffidavitReconciliationSettings
      | undefined;
    requiresAffidavit: boolean;
    mail: MailDelivery[];
    convenienceFee: ConvenienceFee;
    inAppLineItems: LineItem[];
    inAppTaxPct?: number;
    noticePublicationDates: ENotice['publicationDates'] | undefined;
    rate: ESnapshotExists<ERate> | undefined;
    enableAdditivePlacementFee?: boolean;
    /** Resolved expressPlacementBilling for the advertiser org, including any value inherited from a parent org. */
    advertiserExpressPlacementBilling?: ExpressPlacementBilling | null;
    /**
     * Contracted price for a static-priced notice. When set, the difference between it and
     * the real total (subtotal + fees + tax + placement fee) is charged as the Column
     * Reseller Adjustment Fee so the total equals this price. Must be a positive integer number of cents.
     */
    staticPriceInCents?: number;
    /** See `AffidavitPricingData.noticeAddOnQuote`. */
    noticeAddOnQuote?: NoticeAddOnQuote | null;
  }
): InvoicePricingAmounts => {
  const subtotalInCents = data.inAppLineItems.reduce(
    (acc, lineItem) => acc + lineItem.amount,
    0
  );

  const taxPct = data.inAppTaxPct ?? newspaper.data().taxPct ?? 0;

  const taxInCents = calculateTaxFromLineItems(data.inAppLineItems, taxPct);

  const { convenienceFeeSplit } = data.rate?.data() || {};

  const convenienceFee = getInvoiceConvenienceFee(
    newspaper,
    data.advertiserOrganization,
    data.convenienceFee
  );

  const addOnPricingContext = getAddOnPricingContextForNotice(
    {
      noticeAddOnQuote: data.noticeAddOnQuote,
      paperLevelSettings: data.paperLevelAffidavitReconciliationSettings,
      settings: data.affidavitReconciliationSettings
    },
    data.mail
  );
  const [addOnErr, noticeAddOns] = computeNoticeAddOnPricing(
    data.mail,
    data.noticeAddOnQuote?.noticeAddOnPricing ??
      newspaper.data().noticeAddOnPricing ??
      {},
    addOnPricingContext
  );
  if (addOnErr) {
    getErrorReporter().logAndCaptureCriticalError(
      ColumnService.PAYMENTS,
      addOnErr,
      'Failed to compute notice add-on pricing for invoice convenience fee base',
      { newspaperId: newspaper.id }
    );
    throw addOnErr;
  }
  const convenienceFeeSubtotalInCents = getConvenienceFeeSubtotalInCents(
    subtotalInCents,
    noticeAddOns
  );

  const { convenienceFeeInCents, trackedConvenienceFeeSplitResult } =
    calculateConvenienceFeeAndFeeSplit(
      convenienceFeeSubtotalInCents,
      convenienceFee,
      convenienceFeeSplit
    );

  const {
    affidavitFeeInCents: defaultAffidavitFeeInCents,
    invoiceTrackedAffidavitFeeSplitResult
  } = getAffidavitFeeInCentsAndFeeSplitFromSettingsAndMail(
    data.affidavitReconciliationSettings,
    data.mail
  );

  // When a notice add-on (RON or wet sig) covers the affidavit, the legacy
  // per-notice affidavit fee and its fee split must both be 0 — the two are
  // mutually exclusive, so applying both would double-bill the affidavit.
  const addOnCoversAffidavit = noticeAddOnCoversAffidavit(
    noticeAddOns,
    addOnPricingContext
  );

  const affidavitFeeInCents = addOnCoversAffidavit
    ? 0
    : getFinalAffidavitFeeInCents({
        subtotalInCents,
        defaultAffidavitFeeInCents,
        convenienceFee: data.convenienceFee,
        affidavitSettings: data.affidavitReconciliationSettings,
        requiresAffidavit: data.requiresAffidavit,
        publicationDates: data.noticePublicationDates,
        rate: data.rate?.data()
      });

  const applicationFeeInCents = calculateApplicationFeeInCents(
    newspaper.data().applicationFeeSettings,
    subtotalInCents
  );

  const [placementFeeError, placementFeeAmount] =
    computePlacementFeeForInvoicePricing(
      data.enableAdditivePlacementFee,
      data.advertiserExpressPlacementBilling,
      subtotalInCents + convenienceFeeInCents + affidavitFeeInCents
    );
  if (placementFeeError) {
    getErrorReporter().logAndCaptureCriticalError(
      ColumnService.FINANCIAL_RECONCILIATION,
      placementFeeError,
      'Error calculating placement fee for invoice pricing',
      {
        advertiserOrganizationId:
          data.advertiserOrganization?.id || 'undefined',
        newspaperId: newspaper.id
      }
    );
    // we want to interrupt generation of the invoice in this case, so throwing
    throw placementFeeError;
  }

  const placementFeeInCents = placementFeeAmount ?? 0;

  const realTotalInCents =
    subtotalInCents +
    convenienceFeeInCents +
    affidavitFeeInCents +
    taxInCents +
    placementFeeInCents;

  const { staticPriceInCents } = data;
  let resellerAdjustmentFeeInCents = 0;
  if (staticPriceInCents !== undefined) {
    if (!Number.isInteger(staticPriceInCents) || staticPriceInCents <= 0) {
      throw new Error(
        `Invalid static price for invoice pricing: ${staticPriceInCents}`
      );
    }
    resellerAdjustmentFeeInCents = staticPriceInCents - realTotalInCents;
  }

  const totalInCents = realTotalInCents + resellerAdjustmentFeeInCents;

  const publisherAmountInCents = calculatePublisherAmountInCents({
    subtotalInCents,
    taxInCents,
    applicationFeeInCents,
    noticeAddOns
  });
  const publisherFeeSplits = removeUndefinedFields({
    convenienceFeeSplitResult: trackedConvenienceFeeSplitResult,
    autoAffidavitFeeSplitResult: addOnCoversAffidavit
      ? undefined
      : invoiceTrackedAffidavitFeeSplitResult
  });

  return {
    applicationFeeInCents,
    publisherAmountInCents,
    totalInCents,
    subtotalInCents,
    convenienceFeeInCents,
    affidavitFeeInCents,
    taxInCents,
    ...(placementFeeInCents ? { placementFeeInCents } : {}),
    ...(staticPriceInCents !== undefined ? { staticPriceInCents } : {}),
    ...(resellerAdjustmentFeeInCents ? { resellerAdjustmentFeeInCents } : {}),
    ...(Object.keys(publisherFeeSplits).length > 0 && { publisherFeeSplits }),
    ...(noticeAddOns &&
      Object.keys(noticeAddOns).length > 0 && { noticeAddOns })
  };
};

export const getColumnRepFeeFromNewspaper = (
  newspaperSnap: ESnapshotExists<EOrganization>
) => {
  const additionalFees = newspaperSnap.data().additionalFees || [];

  const existingColumnRepFee = additionalFees?.find(
    fee => fee.description === COLUMN_REP_FEE
  );

  return existingColumnRepFee;
};

export const isColumnRepFee = (additionalFee: AdditionalFee) => {
  return additionalFee?.description === COLUMN_REP_FEE;
};

/**
 * Methods only exported for testing
 */
export const __private = {
  distributeDbPrice,
  rateToConvenienceFee,
  getColumnRepFeePctFromNewspaper
};
